import { useTranslation } from 'react-i18next';
import {
  resolveCookiePolicyPdfPath,
  resolveNoticeAtCollectionPdfPath,
  resolvePrivacyPdfPath,
  resolveTermsPdfPath,
} from '../legalLinks';
import { LegalPageLayout } from './LegalPageLayout';
import { LegalCompanyBlock } from './LegalCompanyBlock';

type FaqItem = { q: string; a: string };

export function TermsPage() {
  const { t, i18n } = useTranslation();
  const privacyPdfPath = resolvePrivacyPdfPath(i18n.language);
  const termsPdfPath = resolveTermsPdfPath(i18n.language);
  const cookiePdfPath = resolveCookiePolicyPdfPath(i18n.language);
  const noticePdfPath = resolveNoticeAtCollectionPdfPath(i18n.language);

  const sections = [
    {
      title: t('legal.terms.section1Title'),
      body: t('legal.terms.section1Body'),
    },
    {
      title: t('legal.terms.section2Title'),
      body: t('legal.terms.section2Body'),
    },
    {
      title: t('legal.terms.section3Title'),
      body: t('legal.terms.section3Body'),
    },
  ];

  const documents = [
    {
      title: t('legal.documents.privacyTitle'),
      description: t('legal.documents.privacyDesc'),
      href: privacyPdfPath,
    },
    {
      title: t('legal.documents.termsTitle'),
      description: t('legal.documents.termsDesc'),
      href: termsPdfPath,
    },
    {
      title: t('legal.documents.cookieTitle'),
      description: t('legal.documents.cookieDesc'),
      href: cookiePdfPath,
    },
    {
      title: t('legal.documents.noticeTitle'),
      description: t('legal.documents.noticeDesc'),
      href: noticePdfPath,
    },
  ];

  const faqItemsRaw = t('legal.terms.faqItems', { returnObjects: true });
  const faqItems: FaqItem[] = Array.isArray(faqItemsRaw)
    ? (faqItemsRaw as unknown[]).filter((item): item is FaqItem => {
        if (!item || typeof item !== 'object') return false;
        const it = item as Record<string, unknown>;
        return typeof it.q === 'string' && typeof it.a === 'string';
      })
    : [];

  return (
    <LegalPageLayout
      title={t('legal.terms.title')}
      subtitle={t('legal.terms.subtitle')}
      maxWidthClassName="max-w-6xl"
    >
      <div className="space-y-8">
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-6">
          <p className="text-sm font-semibold text-gray-500 mb-3">
            {t('legal.documents.title')}
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            {documents.map((doc) => (
              <a
                key={doc.title}
                href={doc.href}
                className="flex flex-col gap-2 rounded-xl border border-gray-200 bg-white p-4 transition-all hover:border-teal-300 hover:shadow-lg"
              >
                <span className="font-semibold text-gray-900">{doc.title}</span>
                <span className="text-sm text-gray-600">{doc.description}</span>
              </a>
            ))}
          </div>
        </div>
        <LegalCompanyBlock label={t('legal.terms.providerLabel')} />
        {sections.map((section, idx) => (
          <div key={idx}>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              {section.title}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {section.body}
            </p>
          </div>
        ))}
        {faqItems.length > 0 && (
          <div>
            <h2 className="text-2xl font-semibold text-gray-900 mb-3">
              {t('legal.terms.faqTitle')}
            </h2>
            <p className="text-gray-600 leading-relaxed">
              {t('legal.terms.faqIntro')}
            </p>
            <div className="mt-4 grid gap-3">
              {faqItems.map((item) => (
                <div
                  key={item.q}
                  className="rounded-xl border border-gray-200 bg-gray-50 p-4"
                >
                  <h3 className="font-semibold text-gray-900 mb-2">
                    {item.q}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {item.a}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </LegalPageLayout>
  );
}
